<script lang="ts">
	// Due at close split by driver (UC-60.R7; docs/spec/policy/reports.md,
	// Due at close by driver), under Shift's last close (T29) and Close shift's
	// result (T31): a TablePanel of the store's rows in the order it gave them,
	// then the report's line, that these are this cash point's unsettled
	// deliveries and not cash a driver holds. T29 shows Due at close itself as
	// the first row, with the split one step in under it; T31 shows it among
	// its figures above. A close with no split, such as one an older store
	// wrote, shows nothing here.

	import Row from '../components/Row.svelte';
	import TablePanel from '../components/TablePanel.svelte';
	import { formatMinor } from '../format/money';
	import { message, type Language } from '../i18n/messages';
	import { dueRows, type ClosedShift } from '../till/shift.svelte';

	interface Props {
		language: Language;
		minorDigits: number;
		closed: ClosedShift;
		/** Show Due at close as the first row, the split under it (T29). */
		withTotal?: boolean;
		testId: string;
	}

	let { language, minorDigits, closed, withTotal = false, testId }: Props = $props();

	const rows = $derived(dueRows(language, closed));
</script>

{#if rows.length > 0}
	<div class="due">
		<TablePanel
			columns={[
				{ label: message(language, 'shiftDueOrders'), w: 'minmax(0, 1fr)' },
				{ label: message(language, 'shiftDueAmount'), w: 'auto', align: 'end' },
			]}
			{testId}
		>
			{#if withTotal}
				<Row testId="{testId}-total">
					<span class="label">{message(language, 'shiftDue')} ({closed.dueCount})</span>
					<span class="total figure" dir="ltr">{formatMinor(closed.dueMinor, minorDigits)}</span>
				</Row>
			{/if}
			{#each rows as row (row.key)}
				<Row testId="{testId}-{row.key}">
					<span class:split={withTotal}><bdi>{row.label}</bdi> ({row.count})</span>
					<span class="amount figure" dir="ltr">{formatMinor(row.amountMinor, minorDigits)}</span>
				</Row>
			{/each}
		</TablePanel>
		<p class="note" data-testid="{testId}-note">{message(language, 'shiftDueByDriverNote')}</p>
	</div>
{/if}

<style>
	.due {
		flex: none;
		display: flex;
		flex-direction: column;
		gap: var(--space-4);
	}

	.label {
		font-size: var(--text-sm);
		color: var(--text-muted);
	}

	.total {
		font-family: var(--font-mono);
		font-weight: 700;
	}

	/* Under Due at close, each row one step in. */
	.split {
		padding-inline-start: var(--space-7);
	}

	.amount {
		font-family: var(--font-mono);
		font-weight: 600;
	}

	/* A figure sits at the row's end, under its column's label, in both
	   directions: the cell itself reads left to right. */
	.figure {
		justify-self: end;
	}

	.note {
		margin: 0;
		font-size: var(--text-sm);
		color: var(--text-muted);
	}
</style>
